import { Plugin, type Shape } from '@servicenow/sdk-build-core'
import type { Diagnostics } from '@servicenow/sdk-build-core'
import { TriggerInstanceShape } from '../utils/playbook-shapes'

function getNumericValue(shape: Shape): number | undefined {
    const value = shape.getValue?.()
    const num = typeof value === 'number' ? value : Number(value)
    return Number.isNaN(num) ? undefined : num
}

function validateIntegerRange(shape: Shape, name: string, min: number, max: number, diagnostics: Diagnostics): void {
    const num = getNumericValue(shape)
    if (num === undefined) {
        diagnostics.error(shape, `${name} must be a number`)
    } else if (!Number.isInteger(num) || num < min || num > max) {
        diagnostics.error(shape, `${name} must be an integer between ${min} and ${max}`)
    }
}

const VALID_DAYS_OF_WEEK = /^[1-7]+$/

/**
 * Validates trigger instance inputs and emits diagnostics.
 *
 * This plugin runs before PlaybookTriggerInstancePlugin to catch invalid
 * input values early. It always returns { success: false } since it does
 * not create records.
 */
export const PlaybookTriggerInstanceDiagnosticsPlugin = Plugin.create({
    name: 'PlaybookTriggerInstanceDiagnosticsPlugin',

    shapes: [
        {
            shape: TriggerInstanceShape,
            fileTypes: ['fluent'],

            toRecord(triggerShape, { diagnostics }) {
                const triggerInputs = triggerShape.getTriggerInputs()
                let repeatMonth: number | undefined, dayOfMonth: number | undefined

                for (const [name, shape] of triggerInputs.entries()) {
                    if (shape.isUndefined()) {
                        continue
                    }

                    switch (name) {
                        case 'limit':
                            validateIntegerRange(shape, 'limit', 1, 1000, diagnostics)
                            break
                        case 'frequency':
                            validateIntegerRange(shape, 'frequency', 1, Infinity, diagnostics)
                            break
                        case 'dayOfMonth':
                            dayOfMonth = getNumericValue(shape)
                            break
                        case 'repeatMonth':
                            repeatMonth = getNumericValue(shape)
                            break
                        case 'daysOfTheWeek': {
                            const value = shape.ifString()?.getValue() ?? ''
                            if (!VALID_DAYS_OF_WEEK.test(value)) {
                                diagnostics.error(shape, 'daysOfTheWeek must only contain digits 1-7')
                            } else if (new Set(value).size !== value.length) {
                                diagnostics.error(shape, 'daysOfTheWeek must not contain duplicate digits')
                            }
                            break
                        }
                    }
                }

                if (
                    dayOfMonth !== undefined &&
                    !Number.isNaN(dayOfMonth) &&
                    repeatMonth !== undefined &&
                    !Number.isNaN(repeatMonth)
                ) {
                    if (repeatMonth === 2 && dayOfMonth > 29) {
                        diagnostics.error(triggerShape, 'Cannot choose dayOfMonth > 29 for February')
                    } else if (
                        (repeatMonth === 4 || repeatMonth === 6 || repeatMonth === 9 || repeatMonth === 11) &&
                        dayOfMonth > 30
                    ) {
                        diagnostics.error(
                            triggerShape,
                            'Selected repeatMonth has 30 days, cannot choose dayOfMonth > 30'
                        )
                    }
                }

                return { success: false }
            },
        },
    ],
    docs: [],
})
